/**
 * The one "you have unsaved changes" bar.
 *
 * ── Why this exists ─────────────────────────────────────────────────────
 *
 * The platform console's tenant Configuration tab carried five editable
 * sections and, between them, three different commit affordances: a hand-rolled
 * blue strip on the ceilings, a bare Save/Discard pair on the model allow-list
 * and the display-currency editor, and a sticky `SaveBar` on the two pricing
 * grids. Same idea, three shapes, and only one of them announced itself.
 *
 * That is not a tidiness complaint. The sections differ in LENGTH — the pricing
 * grids are tall enough that the control which commits a typed number can be
 * off-screen when it appears (which is F-14, the one defect in this whole
 * review that could lose an operator's work). A bar that is sticky in one
 * section and static in the next teaches nothing an operator can rely on.
 *
 * ── The invariants ──────────────────────────────────────────────────────
 *
 * 1. **Nothing is rendered while nothing is staged.** A permanently visible bar
 *    says "there is something to do" on a screen someone opened to read; a
 *    permanently DISABLED one says the same thing in grey.
 * 2. **`aria-live`**, because on a long section the bar can appear entirely
 *    off-screen from the field that dirtied it, and the announcement is the
 *    only signal a non-sighted operator gets that anything is pending.
 * 3. **Sticky to the bottom of the viewport.** The reason F-14 happened is that
 *    a bar was `position: static` under a comment calling it sticky, 1,656px
 *    below the field it committed.
 * 4. **Discard comes first, Save last** — the confirming action is where both
 *    the eye and the tab order end up.
 *
 * Copy is overridable because three call sites already own translated,
 * pluralised strings for it; the defaults are the English the console uses.
 */
import type { ReactNode } from 'react'

export interface DirtyBarProps {
  /** How many staged changes. `0` renders nothing at all — see invariant 1. */
  count: number
  /** A write is in flight: both buttons go inert rather than disappearing. */
  busy: boolean
  /**
   * Save alone is refused — the draft is dirty but not valid.
   *
   * SEPARATE from `busy` deliberately. Folding an invalid draft into `busy`
   * would disable Discard too, which is precisely the state in which an
   * operator most wants it: they have typed something the editor will not
   * accept and the way out is to throw it away.
   */
  saveDisabled?: boolean
  onSave: () => void
  onDiscard: () => void
  /** "{{count}} unsaved changes". Given a node, it replaces the default line. */
  pendingLabel?: ReactNode
  saveLabel?: ReactNode
  savingLabel?: ReactNode
  discardLabel?: ReactNode
}

export function DirtyBar({
  count, busy, saveDisabled = false, onSave, onDiscard,
  pendingLabel, saveLabel, savingLabel, discardLabel,
}: DirtyBarProps) {
  if (count === 0) return null
  return (
    <div
      aria-live="polite"
      data-testid="dirty-bar"
      className="sticky bottom-0 z-10 -mx-2 mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-blue-500/40 bg-blue-500/10 px-3 py-2 backdrop-blur-sm"
    >
      <span className="themed-text text-xs">
        {pendingLabel ?? (count === 1 ? '1 unsaved change' : `${count} unsaved changes`)}
      </span>
      <div className="flex-1" />
      <button
        onClick={onDiscard}
        disabled={busy}
        className="px-2.5 py-1 text-xs rounded themed-btn-secondary disabled:opacity-50"
      >
        {discardLabel ?? 'Discard'}
      </button>
      <button
        onClick={onSave}
        disabled={busy || saveDisabled}
        className="px-3 py-1.5 text-xs rounded themed-btn-primary disabled:opacity-50"
      >
        {busy ? (savingLabel ?? 'Saving…') : (saveLabel ?? 'Save')}
      </button>
    </div>
  )
}
